import clsx from "clsx";
import dayjs from "dayjs";
import { DisplayText } from "../DisplayText";
import { SNSShareSection } from "../SNSShareSection";
import { useID } from "../utils";
import type { EventV2Response, ScheduleV2Types } from "@/lib/microcms.server";
import { sortSchedulesByDate } from "@/lib/microcms";

interface EventsArticleProps {
  post: EventV2Response;
  shareURL: string;
  shareText: string;
}

export function EventsArticle({ post, shareText, shareURL }: EventsArticleProps) {
  const displayDate = (dates: ScheduleV2Types[]) => {
    if (dates.length === 1) {
      return dayjs(dates[0].date).locale("ja").format("YYYY/M/D (dd)");
    }
    const sorted = sortSchedulesByDate(dates);
    return `${dayjs(sorted[0].date).locale("ja").format("YYYY/M/D (dd)")} ~ ${dayjs(sorted[sorted.length - 1].date)
      .locale("ja")
      .format("YYYY/M/D (dd)")}`;
  };

  const id = useID();

  return (
    <article className={clsx("relative", "w-full")} itemScope itemType="https://schema.org/Article">
      <header className="mb-6">
        <div className="space-y-2">
          <DisplayText element="h1" itemProp="headline" size="extraLarge">
            {post.title}
          </DisplayText>
          <p>
            <small className="text-red-700">
              * イベント開催中は一般利用ができない場合がありますので予めご了承ください。
            </small>
          </p>

          <div>
            <div className={clsx("flex", "items-center", "flex-wrap", "space-x-4", "mt-8")}>
              <div className={clsx("max-w-full", "flex-grow")}>
                <div className={clsx("font-bold", "text-base", "leading-normal", "flex")}>
                  <span className={clsx("block", "text-gray-700")}>
                    <time itemProp="contentReferenceTime" dateTime={sortSchedulesByDate(post.schedules)[0].date}>
                      {displayDate(post.schedules)}
                    </time>
                  </span>
                  <span aria-hidden="true" className={clsx("block", "text-gray-700", "mx-2")}>
                    ・
                  </span>
                  <span
                    itemProp="contentLocation"
                    className={clsx(
                      "block",
                      (post.venue[0] === "屋内" || post.venue[0] === "屋内半面" || post.venue[0] === "屋内全面") &&
                        "text-brand-green",
                      post.venue[0] === "屋外" && "text-brand-red",
                      post.venue[0] === "屋内外（両方）" && "text-brand-purple"
                    )}
                  >
                    <span className="visually-hidden">万代島多目的広場大かま: </span>
                    {post.venue[0]}
                  </span>
                </div>
              </div>
              <div className="min-w-0">
                <SNSShareSection url={shareURL} text={shareText} />
              </div>
            </div>
          </div>
        </div>
      </header>

      {post.contents?.main_image && (
        <figure className={clsx("mt-4", "mb-8")}>
          <meta itemProp="image" content={post.contents.main_image.url} />
          <picture>
            <source
              type="image/webp"
              srcSet={`
                ${post.contents.main_image.url}?fm=webp&fit=crop&crop=center,center&w=220 320w,
                ${post.contents.main_image.url}?fm=webp&fit=crop&crop=center,center&w=450 450w,
                ${post.contents.main_image.url}?fm=webp&fit=crop&crop=center,center&w=600 600w
              `}
            />
            <img
              className={clsx("max-w-full", "w-full", "sm:w-4/5", "sm:mx-auto")}
              sizes="100vw"
              srcSet={`
                ${post.contents.main_image.url}?fit=crop&crop=center,center&w=220 320w,
                ${post.contents.main_image.url}?fit=crop&crop=center,center&w=450 450w,
                ${post.contents.main_image.url}?fit=crop&crop=center,center&w=600 600w
              `}
              src={`${post.contents.main_image.url}?fit=crop&crop=center,center&w=600`}
              alt={`${post.title}のメインビジュアル画像`}
            />
          </picture>
        </figure>
      )}
      {post.contents?.description && (
        <div className={clsx("mt-4", "mb-6")}>
          <div className="space-y-2">
            <DisplayText element="h2" size="medium">
              詳細情報
            </DisplayText>
            <div
              className={clsx("rte", "text-sm", "leading-normal")}
              dangerouslySetInnerHTML={{ __html: post.contents.description }}
            />
          </div>
        </div>
      )}

      {(post.contents?.contact_email || post.contents?.contact_tel || post.contents?.link) && (
        <div className={clsx("mt-6", "mb-8")}>
          <div className="space-y-2">
            <DisplayText element="h2" size="medium">
              このイベントへのお問い合わせ
            </DisplayText>
            <dl>
              {post.contents?.contact_email && (
                <div className={clsx("flex", "items-center", "mb-2")}>
                  <dt
                    className={clsx(
                      "bg-blue-100",
                      "py-0.5",
                      "px-2",
                      "mr-4",
                      "min-w-[32%]",
                      "text-center",
                      "rounded",
                      "font-bold",
                      "sm:min-w-[160px]"
                    )}
                  >
                    <span className="visually-hidden">お問い合わせ先</span>メールアドレス
                  </dt>
                  <dd className="flex-grow">
                    <a
                      className={clsx("text-blue-600", "hover:underline")}
                      href={`mailto:${post.contents.contact_email}`}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {post.contents.contact_email}
                    </a>
                  </dd>
                </div>
              )}
              {post.contents?.contact_tel && (
                <div className={clsx("flex", "items-center", "mb-2")}>
                  <dt
                    className={clsx(
                      "bg-blue-100",
                      "py-0.5",
                      "px-2",
                      "mr-4",
                      "min-w-[32%]",
                      "text-center",
                      "rounded",
                      "font-bold",
                      "sm:min-w-[160px]"
                    )}
                  >
                    <span className="visually-hidden">お問い合わせ先</span>電話番号
                  </dt>
                  <dd className="flex-grow">
                    <a
                      className={clsx("text-blue-600", "hover:underline")}
                      href={`tel:${post.contents.contact_tel}`}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {post.contents.contact_tel}
                    </a>
                  </dd>
                </div>
              )}
              {post.contents?.link && (
                <div className={clsx("flex", "items-center", "mb-2")}>
                  <dt
                    className={clsx(
                      "bg-blue-100",
                      "py-0.5",
                      "px-2",
                      "mr-4",
                      "min-w-[32%]",
                      "text-center",
                      "rounded",
                      "font-bold",
                      "sm:min-w-[160px]"
                    )}
                  >
                    URL
                  </dt>
                  <dd>
                    <a
                      className={clsx("text-blue-600", "hover:underline")}
                      href={post.contents.link}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {post.contents.link}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      <div className={clsx("mt-6", "mb-8")}>
        <div className="space-y-4">
          <DisplayText element="h2" size="medium">
            施設利用時間
          </DisplayText>
          <ul className="list-none">
            {post.schedules?.map((schedule) => {
              return (
                <li key={id} className={clsx("flex", "items-center", "flex-wrap", "mb-2")}>
                  <h3
                    className={clsx(
                      "bg-blue-100",
                      "py-0.5",
                      "px-2",
                      "mr-4",
                      "min-w-[32%]",
                      "text-center",
                      "rounded",
                      "font-bold",
                      "sm:min-w-[160px]"
                    )}
                  >
                    {dayjs(schedule.date).locale("ja").format("YYYY年M月D日 (dd)")}
                  </h3>
                  <p className="flex-grow">
                    {schedule.start_time} ~ {schedule.end_time}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {post.contents?.note && (
        <div className={clsx("mt-4", "mb-6")}>
          <div className="space-y-2">
            <DisplayText element="h2" size="medium">
              その他の情報
            </DisplayText>
            <div
              className={clsx("rte", "text-sm", "leading-normal")}
              dangerouslySetInnerHTML={{ __html: post.contents.note }}
            />
          </div>
        </div>
      )}
    </article>
  );
}
